import React, {useRef, useState, useEffect, useContext} from "react";
import {
    MapContainer,
    ScaleControl,
    Marker,
    TileLayer,
    WMSTileLayer,
    ZoomControl
} from "react-leaflet";
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {Fade} from "reactstrap";
import LangContext from "../providers/LangContext";

<style>
{`
@media (max-width: 768px) {
  .legend-img {
    max-height: 90vh !important;
  }
}
`}
</style>

const MAP_TYPES = {
    cadastral_map: 'cadastral_map',
    zoning_plan: 'zoning_plan',
};

const CadastralMap = ({ lat, lng }) => {
    const mapRef = useRef();
    const [mapType, setMapType] = useState(MAP_TYPES.cadastral_map);
    const [state, setState] = useState({
        map: mapType,
        legend: false,
        sidebar: window.innerWidth > 400
    });

    const {lang} = useContext(LangContext);

    useEffect(() => {
        setState(prev => ({ ...prev, map: mapType }));
    }, [mapType]);

    const toggleLegend = () => {
        setState(prev => ({
            ...prev,
            legend: !prev.legend
        }));
    };

    const markerIcon = new Icon({
        iconUrl: "/images/mapsicon.png",
        iconSize: [28, 45],
        iconAnchor: [14, 45],
        className: "marker-default"
    });

    const changeMap = (type) => {
        setMapType(type);
        if (mapRef.current && mapRef.current._zoom < 18) {
            mapRef.current.setZoom(18);
        }
        if (type === MAP_TYPES.cadastral_map) {
            setState(prev => ({
                ...prev,
                legend: false
            }));
        }
    };

    return (
        <div style={{ position: 'relative' }}>
            <div
                style={{
                    position: 'absolute',
                    left: 10,
                    top: 10,
                    zIndex: 1000,
                }}
                className="btn-group"
            >
                <button
                    className={`btn btn-sm ${mapType === MAP_TYPES.cadastral_map ? 'btn-primary' : 'btn-outline-secondary bg-white text-secondary'} mr-1`}
                    onClick={() => changeMap(MAP_TYPES.cadastral_map)}
                >
                    {lang.get('portal.cadastral_map')}
                </button>
                <button
                    className={`btn btn-sm ${mapType === MAP_TYPES.zoning_plan ? 'btn-primary' : 'btn-outline-secondary bg-white text-secondary'}`}
                    onClick={() => changeMap(MAP_TYPES.zoning_plan)}
                >
                    {lang.get('portal.zoning_plan')}
                </button>
            </div>

            <div
                style={{
                    position: 'absolute',
                    right: 10,
                    bottom: 20,
                    zIndex: 1000,
                }}>
                {mapType === MAP_TYPES.zoning_plan && (
                    <button className="btn btn-sm btn-outline-secondary ml-1 text-secondary bg-white" onClick={toggleLegend}>
                        {lang.get('portal.map_legend')}
                    </button>
                )}
            </div>

            <MapContainer
                ref={mapRef}
                id={'map'}
                style={{ height: 'calc(100vh - 250px)', width: '100%' }}
                center={[lat, lng]}
                maxZoom={22}
                minZoom={10}
                zoom={19}
                dragging={false}
                zoomControl={false}
                doubleClickZoom={false}
                scrollWheelZoom={false}
                attributionControl={true}
                fullscreenControl={true}
            >
                <TileLayer
                    attribution='&copy; <a href="https://gogary.com/" target="_blank">GoGary</a>'
                    url="https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/standaard/EPSG:3857/{z}/{x}/{y}.png"
                    maxZoom={22}
                    minZoom={10}
                    transparent={true}
                />

                {mapType === MAP_TYPES.cadastral_map && (
                    <WMSTileLayer
                        attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                        url="https://service.pdok.nl/kadaster/kadastralekaart/wms/v5_0?"
                        layers='kadastralekaart'
                        maxZoom={22}
                        minZoom={10}
                        format={'image/png'}
                        transparent={true}
                        reuseTiles={true}
                    />
                )}

                {mapType === MAP_TYPES.zoning_plan && (
                    <>
                        <WMSTileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://service.pdok.nl/kadaster/plu/wms/v1_0?"
                            layers='Enkelbestemming,Bouwvlak,Maatvoering,Lettertekenaanduiding,Bouwaanduiding'
                            format={'image/png'}
                            transparent={true}
                            maxZoom={22}
                            minZoom={10}
                        />
                        <WMSTileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://service.pdok.nl/kadaster/kadastralekaart/wms/v5_0?"
                            layers='kadastralekaart'
                            format={'image/png'}
                            transparent={true}
                            maxZoom={22}
                            minZoom={10}
                            reuseTiles={true}
                        />
                    </>
                )}

                <Marker position={[lat, lng]} icon={markerIcon} />
                <ScaleControl position="bottomleft" />
                <ZoomControl position="bottomleft" />
            </MapContainer>

            {!!state.legend &&
                <Fade in={!!state.legend} appear={true} onClick={toggleLegend}>
                    <div
                        className="rounded p-1"
                        style={{
                            position: 'fixed',
                            right: 35,
                            bottom: 10,
                            background: '#c5cbcc',
                            zIndex: 9999
                        }}
                    >
                        <div className="rounded p-2 position-relative" style={{ backgroundColor: 'white' }}>
                            <button
                                aria-label="Close legend"
                                style={{
                                    position: 'absolute',
                                    top: 5,
                                    right: 2,
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: 22,
                                    cursor: 'pointer',
                                    lineHeight: 1,
                                    color: '#333',
                                }}
                            >
                                &times;
                            </button>
                            <img className="legend-img" style={{ maxHeight: 'max(60vh, 600px)' }} src="/images/cadastral-map-legend.png" alt="Legenda" />
                        </div>
                    </div>
                </Fade>
            }
        </div>
    );
};

export default CadastralMap;
